import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import {
  CityListItem,
  cityListQuery,
} from "@/app/Cities/Cities/GraphQL/cityListQuery";
import { useTranslations } from "use-intl";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { Button } from "@/components/catalyst/button";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { CityCreateDialog } from "@/app/Cities/Cities/Components/CityCreateDialog";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ImageIcon } from "lucide-react";
import { QueryCitiesOrderByColumn, SortOrder } from "@/gql/graphql";

type CityTableColumnId = "city" | "slug" | "state" | "region";

export function CityTable({ id }: { id: TableId }) {
  const t = useTranslations("city");
  const { open } = useDialog(CityCreateDialog);

  const columns: DataTableColDef<CityListItem, any, CityTableColumnId>[] = [
    {
      id: "city",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="City" />
      ),
      accessorFn: (data) => data.name,
      cell: ({ row }) => (
        <ModelLinkCell
          from="/$tenant/$orgId/supplier/{-$supplierId}"
          to="/$tenant/$orgId/supplier/{-$supplierId}/cities/$id"
          params={(prev) => ({ ...prev, id: row.original.id })}
          viewTransition
        >
          <div className="flex items-center gap-3">
            {row.original.heroImage?.url ? (
              <img
                src={row.original.heroImage?.url}
                alt={row.original.name}
                className="h-8 w-8 rounded object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded bg-gray-100">
                <ImageIcon className="h-4 w-4 text-gray-400" />
              </div>
            )}
            {row.original.name}
          </div>
        </ModelLinkCell>
      ),
    },
    {
      id: "slug",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Slug" />
      ),
      accessorFn: (data) => data.slug,
    },
    {
      id: "state",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="State" />
      ),
      accessorFn: (data) => t(`state.${data.state!}`),
    },
    {
      id: "region",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Region" />
      ),
      accessorFn: (data) => t(`region.${data.region}`),
    },
  ];

  return (
    <DataTable
      columns={columns}
      document={cityListQuery}
      rightButtons={
        <Button
          LeadingIcon={PlusIcon}
          onClick={() => {
            open({});
          }}
        >
          Add city
        </Button>
      }
      getQueryVariables={({ search, sorting, pagination }) => {
        const baseParams = {
          search,
          page: pagination.pageIndex,
          first: pagination.pageSize,
        };

        if (search) {
          return baseParams;
        }

        return {
          ...baseParams,
          orderBy: sorting
            .map((sort) => {
              const order = sort.desc ? SortOrder.Desc : SortOrder.Asc;
              if (sort.id === "city") {
                return {
                  column: QueryCitiesOrderByColumn.Name,
                  order,
                };
              }

              if (sort.id === "state") {
                return {
                  column: QueryCitiesOrderByColumn.State,
                  order,
                };
              }

              if (sort.id === "slug") {
                return {
                  column: QueryCitiesOrderByColumn.Slug,
                  order,
                };
              }

              if (sort.id === "region") {
                return {
                  column: QueryCitiesOrderByColumn.Region,
                  order,
                };
              }

              console.warn(`No sorting for ${sort.id}`);
              return null;
            })
            .filter(Boolean),
        };
      }}
      accessor={(data) => data.cities}
      id={id}
      initialSorting={[{ id: "city", desc: false }]}
    />
  );
}
